import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useDataProvider, usePermissions } from "react-admin";
import { Outlet } from "react-router-dom";

import { ResourceName } from "../constants/resources";
import type { CustomDataProvider } from "../providers/dataProvider";
import { DailyRow, DistributionRow, isRelevantAppId } from "./utils";

const ANALYTICS_LOG_PREFIX = "[admin-portal analytics]";

type AnalyticsContextValue = {
  dailyRows: DailyRow[];
  distRows: DistributionRow[];
  loading: boolean;
  error: boolean;
};

const AnalyticsContext = createContext<AnalyticsContextValue>({
  dailyRows: [],
  distRows: [],
  loading: false,
  error: false,
});

export const useAnalyticsData = () => useContext(AnalyticsContext);

export const AnalyticsProvider = ({ children }: { children?: ReactNode }) => {
  const { permissions, isPending } = usePermissions();
  const dataProvider = useDataProvider<CustomDataProvider>();
  const isAuthorized = !isPending && !!permissions?.includes(ResourceName.Analytics);

  const [dailyRows, setDailyRows] = useState<DailyRow[]>([]);
  const [distRows, setDistRows] = useState<DistributionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const fetchAll = useCallback(async () => {
    console.log(ANALYTICS_LOG_PREFIX, "fetch start");
    setLoading(true);
    setError(false);
    try {
      const [dailies, distributions] = await Promise.all([
        dataProvider.getAnalyticsDailies(),
        dataProvider.getAnalyticsDistributions(),
      ]);
      const filteredDailies = dailies.filter(r => isRelevantAppId(r.appId));
      const filteredDist = distributions.filter(r => isRelevantAppId(r.appId));
      setDailyRows(filteredDailies);
      setDistRows(filteredDist);
      const dailiesDropped = dailies.length - filteredDailies.length;
      const distDropped = distributions.length - filteredDist.length;
      console.log(ANALYTICS_LOG_PREFIX, "fetch success", {
        dailiesRawCount: dailies.length,
        dailiesAfterRelevanceFilter: filteredDailies.length,
        dailiesDroppedByRelevanceFilter: dailiesDropped,
        distRawCount: distributions.length,
        distAfterRelevanceFilter: filteredDist.length,
        distDroppedByRelevanceFilter: distDropped,
        sampleDailyDates: filteredDailies.slice(0, 5).map(r => r.date.slice(0, 10)),
        sampleAppIds: filteredDailies.slice(0, 5).map(r => r.appId),
      });
    } catch (err) {
      console.error(ANALYTICS_LOG_PREFIX, "fetch failed", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [dataProvider]);

  useEffect(() => {
    if (isPending) return;
    if (isAuthorized) {
      void fetchAll();
    } else {
      console.log(ANALYTICS_LOG_PREFIX, "skip fetch: not authorized for Analytics", {
        permissions,
      });
    }
  }, [isPending, isAuthorized, fetchAll, permissions]);

  const value = useMemo(() => ({ dailyRows, distRows, loading, error }), [dailyRows, distRows, loading, error]);

  return <AnalyticsContext.Provider value={value}>{children ?? <Outlet />}</AnalyticsContext.Provider>;
};
